<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const avisos = ref([]);
const hoje = ref('');
const formulario = ref(null);
const excluindo = ref(null);
const salvando = ref(false);

const vazio = () => ({
  id: null,
  tipo: 'aviso',
  titulo: '',
  texto: '',
  inicio: hoje.value,
  fim: hoje.value,
});

const falhou = e =>
  useAlert(e?.response?.data?.error || t('AGENTE_IA.BASE.AVISOS.ERRO'));

const aplicar = ({ data }) => {
  avisos.value = data.payload;
  hoje.value = data.hoje;
};

const avisarSalvo = ({ data }, mensagem) =>
  useAlert(
    data.sincronizacao_pendente ? t('AGENTE_IA.BASE.AVISOS.PENDENTE') : mensagem
  );

onMounted(async () => {
  try {
    aplicar(await AgenteIaAPI.avisos());
  } catch (e) {
    falhou(e);
  }
});

const novo = () => {
  excluindo.value = null;
  formulario.value = vazio();
};

const editar = aviso => {
  excluindo.value = null;
  formulario.value = { ...aviso };
};

const formatarData = iso => iso.split('-').reverse().join('/');

const salvar = async () => {
  const { id, ...atributos } = formulario.value;
  salvando.value = true;
  try {
    const resposta = id
      ? await AgenteIaAPI.atualizarAviso(id, atributos)
      : await AgenteIaAPI.criarAviso(atributos);
    aplicar(resposta);
    formulario.value = null;
    avisarSalvo(resposta, t('AGENTE_IA.BASE.AVISOS.SALVO'));
  } catch (e) {
    falhou(e);
  } finally {
    salvando.value = false;
  }
};

const excluir = async aviso => {
  salvando.value = true;
  try {
    const resposta = await AgenteIaAPI.apagarAviso(aviso.id);
    aplicar(resposta);
    excluindo.value = null;
    avisarSalvo(resposta, t('AGENTE_IA.BASE.AVISOS.EXCLUIDO'));
  } catch (e) {
    falhou(e);
  } finally {
    salvando.value = false;
  }
};

const classeCampo =
  'w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12';
const classeSituacao = {
  vigente: 'text-n-teal-11',
  agendado: 'text-n-amber-11',
  vencido: 'text-n-slate-11',
};
</script>

<template>
  <section class="flex flex-col gap-3">
    <p class="text-xs text-n-slate-11">
      {{ t('AGENTE_IA.BASE.AVISOS.AJUDA') }}
    </p>
    <Button
      :label="t('AGENTE_IA.BASE.AVISOS.NOVO')"
      :disabled="salvando"
      @click="novo"
    />
    <div
      v-if="formulario"
      class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
    >
      <select
        v-model="formulario.tipo"
        :class="classeCampo"
        :aria-label="t('AGENTE_IA.BASE.AVISOS.TIPO')"
      >
        <option value="aviso">
          {{ t('AGENTE_IA.BASE.AVISOS.TIPOS.aviso') }}
        </option>
        <option value="promocao">
          {{ t('AGENTE_IA.BASE.AVISOS.TIPOS.promocao') }}
        </option>
      </select>
      <input
        v-model="formulario.titulo"
        maxlength="120"
        :class="classeCampo"
        :aria-label="t('AGENTE_IA.BASE.AVISOS.TITULO_CAMPO')"
        :placeholder="t('AGENTE_IA.BASE.AVISOS.TITULO_CAMPO')"
      />
      <textarea
        v-model="formulario.texto"
        rows="3"
        maxlength="1000"
        :class="classeCampo"
        :aria-label="t('AGENTE_IA.BASE.AVISOS.TEXTO_CAMPO')"
        :placeholder="t('AGENTE_IA.BASE.AVISOS.TEXTO_CAMPO')"
      />
      <div class="flex flex-wrap gap-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('AGENTE_IA.BASE.AVISOS.INICIO') }}
          <input v-model="formulario.inicio" type="date" :class="classeCampo" />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('AGENTE_IA.BASE.AVISOS.FIM') }}
          <input
            v-model="formulario.fim"
            type="date"
            :min="formulario.inicio"
            :class="classeCampo"
          />
        </label>
      </div>
      <div class="flex gap-2">
        <Button
          size="sm"
          :label="t('AGENTE_IA.BASE.AVISOS.SALVAR')"
          :is-loading="salvando"
          :disabled="
            salvando ||
            !formulario.titulo.trim() ||
            !formulario.texto.trim() ||
            !formulario.inicio ||
            !formulario.fim
          "
          @click="salvar"
        />
        <Button
          size="sm"
          variant="outline"
          :label="t('AGENTE_IA.BASE.AVISOS.CANCELAR')"
          :disabled="salvando"
          @click="formulario = null"
        />
      </div>
    </div>
    <p v-if="!avisos.length" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.BASE.AVISOS.VAZIO') }}
    </p>
    <ul class="flex flex-col gap-2">
      <li
        v-for="aviso in avisos"
        :key="aviso.id"
        class="flex flex-col gap-1 p-3 border rounded-lg border-n-weak"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ t(`AGENTE_IA.BASE.AVISOS.TIPOS.${aviso.tipo}`) }}:
            {{ aviso.titulo }}
          </span>
          <span class="text-xs" :class="classeSituacao[aviso.situacao]">
            {{ t(`AGENTE_IA.BASE.AVISOS.SITUACAO.${aviso.situacao}`) }}
          </span>
        </div>
        <p class="text-sm whitespace-pre-wrap text-n-slate-12">
          {{ aviso.texto }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{
            t('AGENTE_IA.BASE.AVISOS.PERIODO', {
              inicio: formatarData(aviso.inicio),
              fim: formatarData(aviso.fim),
            })
          }}
        </p>
        <div class="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            :label="t('AGENTE_IA.BASE.AVISOS.EDITAR')"
            :disabled="salvando"
            @click="editar(aviso)"
          />
          <Button
            size="sm"
            variant="outline"
            :label="t('AGENTE_IA.BASE.AVISOS.EXCLUIR')"
            :disabled="salvando"
            @click="excluindo = aviso"
          />
        </div>
        <div v-if="excluindo?.id === aviso.id" class="flex flex-col gap-2">
          <p class="text-sm text-n-slate-12">
            {{
              t('AGENTE_IA.BASE.AVISOS.CONFIRMAR_EXCLUSAO', {
                titulo: aviso.titulo,
              })
            }}
          </p>
          <div class="flex gap-2">
            <Button
              size="sm"
              :label="t('AGENTE_IA.BASE.AVISOS.EXCLUIR')"
              :is-loading="salvando"
              :disabled="salvando"
              @click="excluir(aviso)"
            />
            <Button
              size="sm"
              variant="outline"
              :label="t('AGENTE_IA.BASE.AVISOS.CANCELAR')"
              :disabled="salvando"
              @click="excluindo = null"
            />
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>
